import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, ArrowLeft, Check } from "lucide-react"
import { useRef } from "react"
import { Link, useParams } from "react-router"
import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * Detalle de una cotización con sus ítems.
 *
 * **Sin actualizaciones optimistas.** Aprobar puede ser rechazado por el servidor
 * —ya está aprobada, o el rol no alcanza— y pintar un éxito que el servidor va a
 * negar es el peor caso posible: el usuario cree que cerró el trato. Se espera la
 * respuesta y se invalida la consulta.
 */

type EstadoCotizacion = "BORRADOR" | "ENVIADA" | "APROBADA" | "RECHAZADA"

interface Item {
  id: number
  tipo: "GASTO" | "SERVICIO" | "IMPUESTO"
  concepto: string
  cantidad: string
  precio_unitario: string
  moneda: "USD" | "BS"
  orden: number
}

interface Detalle {
  id: number
  numero: string
  cliente_id: number
  aduana_id: number
  estado: EstadoCotizacion
  tasa_cambio: string
  notas: string | null
  creado_por: number
  creado_en: string
  items: Item[]
  total_usd: string
}

export function CotizacionDetalle() {
  const { id } = useParams<{ id: string }>()
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const vista = useRef<HTMLDivElement | null>(null)

  const consulta = useQuery({
    queryKey: ["cotizacion", id],
    queryFn: () => api.get<Detalle>(`/api/v1/cotizaciones/${id}`),
    enabled: Boolean(id),
  })

  const aprobar = useMutation({
    mutationFn: () => api.post<Detalle>(`/api/v1/cotizaciones/${id}/aprobar`),
    onSuccess: () => {
      // Se invalidan las dos claves afectadas: el detalle y la bandeja, donde
      // esta fila cambia de estado.
      clienteQuery.invalidateQueries({ queryKey: ["cotizacion", id] })
      clienteQuery.invalidateQueries({ queryKey: ["cotizaciones"] })
    },
  })

  if (consulta.isPending) {
    return (
      <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando cotización…
      </p>
    )
  }

  if (consulta.isError) {
    const esNoEncontrado = consulta.error instanceof ErrorApi && consulta.error.estado === 404
    return (
      <div role="alert" className="py-12 text-center" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={24} aria-hidden="true" className="mx-auto mb-3" />
        <p>{esNoEncontrado ? "Esa cotización no existe" : "No se pudo cargar la cotización"}</p>
        <Link to="/cotizaciones" className="mt-4 inline-block text-sm underline">
          Volver a la bandeja
        </Link>
      </div>
    )
  }

  const cot = consulta.data
  const puedeAprobar =
    tienePermiso(usuario, "cotizacion:aprobar") &&
    (cot.estado === "BORRADOR" || cot.estado === "ENVIADA")

  return (
    <div>
      <Link
        to="/cotizaciones"
        className="mb-4 inline-flex items-center gap-2 text-sm"
        style={{ color: "var(--fg-muted)" }}
      >
        <ArrowLeft size={16} aria-hidden="true" />
        Cotizaciones
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{cot.numero}</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            {cot.estado} · tasa <span className="tabular">{cot.tasa_cambio}</span> Bs/USD
          </p>
        </div>

        <div className="flex flex-wrap items-start gap-2">
          <Exportar
            titulo={`Cotización ${cot.numero}`}
            subtitulo={`${cot.estado} · tasa ${cot.tasa_cambio} Bs/USD`}
            contenido={vista}
          />
          {puedeAprobar && (
            <button
              type="button"
              onClick={() => aprobar.mutate()}
              disabled={aprobar.isPending}
              className="flex h-9 items-center gap-2 rounded-[var(--radius-control)] px-3 text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: "var(--primary)", color: "var(--primary-fg)" }}
            >
              <Check size={16} aria-hidden="true" />
              {aprobar.isPending ? "Aprobando…" : "Aprobar"}
            </button>
          )}
        </div>
      </div>

      {aprobar.isError && (
        <p role="alert" className="mb-4 text-sm" style={{ color: "var(--destructive)" }}>
          {aprobar.error instanceof ErrorApi
            ? aprobar.error.message
            : "No se pudo aprobar la cotización"}
        </p>
      )}

      {cot.items.length === 0 ? (
        <div className="superficie py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
          Esta cotización no tiene ítems
        </div>
      ) : (
        <div ref={vista}>
          <Tabla etiqueta="Ítems de la cotización">
            <Cabecera>
              <Th>Concepto</Th>
              <Th>Tipo</Th>
              <Th derecha>Cantidad</Th>
              <Th derecha>Precio</Th>
              <Th>Moneda</Th>
            </Cabecera>
            <Cuerpo>
              {cot.items.map((item) => (
                <Fila key={item.id}>
                  <Td>{item.concepto}</Td>
                  <Td style={{ color: "var(--fg-muted)" }}>{item.tipo}</Td>
                  <Td derecha numero data-exportar-valor={item.cantidad.replace(".", ",")}>
                    {item.cantidad}
                  </Td>
                  <Td derecha numero data-exportar-valor={item.precio_unitario.replace(".", ",")}>
                    {item.precio_unitario}
                  </Td>
                  <Td>{item.moneda}</Td>
                </Fila>
              ))}
            </Cuerpo>
            <tfoot>
              <tr className="border-t" style={{ borderColor: "var(--border)" }}>
                <Td derecha colSpan={3} className="font-medium">
                  Total
                </Td>
                <Td
                  derecha
                  numero
                  className="font-semibold"
                  data-exportar-valor={cot.total_usd.replace(".", ",")}
                >
                  {cot.total_usd}
                </Td>
                <Td>USD</Td>
              </tr>
            </tfoot>
          </Tabla>
        </div>
      )}

      {cot.notas && (
        <div className="tarjeta mt-6">
          <h2 className="mb-2 text-sm font-medium">Notas</h2>
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            {cot.notas}
          </p>
        </div>
      )}
    </div>
  )
}
